/*
 * pdp-search.js
 * ---------------------------------------------------------------
 * The search drawer.
 *
 * Two things carried over from building the navigation drawer, both
 * learned the hard way:
 *
 *   - the panel is moved to <body>. Inside the header, a `transform` or
 *     a `backdrop-filter` on any ancestor becomes the containing block
 *     for a `position: fixed` child, and the sticky header has both --
 *     the panel would have been the height of the header.
 *   - the page is locked on the root element, never on <body>.
 *     `overflow: hidden` on body makes body a scroll container, which
 *     stops `position: sticky` working on the header.
 * ---------------------------------------------------------------
 */
class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('[data-sdrawer-toggle]');
    this.panel = this.querySelector('[data-sdrawer-panel]');
    this.overlay = this.querySelector('[data-sdrawer-overlay]');
    this.open = false;

    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.measure = this.measure.bind(this);
  }

  connectedCallback() {
    if (!this.toggle || !this.panel) return;

    /* Both the panel and its overlay go to <body>, in that order, so the
       overlay stays behind the panel. */
    [this.overlay, this.panel].forEach((el) => {
      if (!el || el.parentElement === document.body) return;
      const stale = document.body.querySelector(`:scope > [${el === this.panel ? 'data-sdrawer-panel' : 'data-sdrawer-overlay'}]`);
      if (stale && stale !== el) stale.remove();
      document.body.appendChild(el);
    });

    this.input = this.panel.querySelector('[data-sdrawer-input]');
    this.clear = this.panel.querySelector('[data-sdrawer-clear]');
    this.live = this.panel.querySelector('[data-sdrawer-live]');
    this.reco = this.panel.querySelector('[data-sdrawer-reco]');
    this.suggestUrl = this.getAttribute('data-suggest-url') || '/search/suggest';
    this.cache = new Map();

    this.toggle.addEventListener('click', () => this.setOpen(!this.open));
    this.panel.querySelector('[data-sdrawer-close]').addEventListener('click', () => this.setOpen(false, true));
    if (this.overlay) this.overlay.addEventListener('click', () => this.setOpen(false));

    if (this.input) {
      this.input.addEventListener('input', () => {
        this.syncClear();
        this.queueSearch();
      });
      this.syncClear();
    }

    if (this.clear) {
      this.clear.addEventListener('click', () => {
        this.input.value = '';
        this.syncClear();
        this.showRecommendations();
        this.input.focus();
      });
    }

    window.addEventListener('resize', this.onResize);
    window.addEventListener('resize', this.measure);
    /* The mobile toolbar slides in and out without firing `resize`. */
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.measure);
      window.visualViewport.addEventListener('scroll', this.measure);
    }
    this.measure();
  }

  /* Pin the panel to what is actually visible.
   *
   * Two separate things go wrong on a phone, and `bottom: 0` only
   * addresses one of them:
   *
   *   - iOS puts `position: fixed; bottom: 0` at the bottom of the
   *     *layout* viewport, which sits behind the Safari toolbar. The
   *     panel stops short and the page shows underneath it.
   *   - raising the keyboard shrinks the visual viewport AND scrolls the
   *     layout viewport, which drags a fixed panel up off the top of the
   *     screen. That is the "Search" heading disappearing.
   *
   * `visualViewport` reports both: `offsetTop` is how far the layout
   * viewport has been pushed, `height` is what is left to see. Setting
   * the panel's top and height from those pins it to the visible area
   * whatever the browser is doing around it.
   *
   * Everywhere without a soft keyboard these are 0 and the full height,
   * so nothing changes. */
  measure() {
    // Height is no longer taken from the visual viewport.
    //
    // vv.height is what is left AFTER the toolbar and the keyboard have
    // taken their share, so the panel shrank whenever either appeared --
    // opening the keyboard in search halved the drawer, and the cart lost
    // height whenever the address bar expanded. The CSS uses 100svh
    // instead: the small viewport is a stable number that already excludes
    // the browser UI, so the panel is full height and stays there.
    //
    // offsetTop is still tracked. That one is not about size: it is how far
    // the layout viewport has been scrolled behind the visual one, and
    // without it a fixed panel drifts off the top of the screen.
    const vv = window.visualViewport;
    const top = vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
    const root = document.documentElement.style;
    root.setProperty('--sdrawer-top', `${top}px`);
    root.removeProperty('--sdrawer-h');
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('resize', this.measure);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.measure);
      window.visualViewport.removeEventListener('scroll', this.measure);
    }
    this.unlock();
  }

  /* The clear button only exists while there is something to clear. */
  syncClear() {
    if (!this.clear) return;
    this.clear.hidden = this.input.value.trim() === '';
  }

  setOpen(next, fromKeyboard) {
    if (next === this.open) return;
    this.open = next;

    this.toggle.setAttribute('aria-expanded', String(next));
    /* Applied synchronously, so the reflow below sees a visible panel. */
    this.panel.classList.toggle('is-open', next);
    if (this.overlay) {
      this.overlay.hidden = !next;
      requestAnimationFrame(() => this.overlay.classList.toggle('is-open', next));
    }
    document.documentElement.classList.toggle('sdrawer-open', next);

    if (next) {
      /* Measured on every open: the toolbar may have collapsed since the
         last time, and the keyboard may be about to appear. */
      this.measure();
      this.lock();
      document.addEventListener('keydown', this.onKeydown);
      /* The field is not focused on open, so the keyboard stays down.
       *
       * The drawer opens onto recommendations, and those are worth seeing
       * -- half the screen disappearing under a keyboard before anyone
       * has decided to type hides the thing the panel opened to show. The
       * field is one tap away for anyone who did come here to search.
       *
       * Focus still moves into the panel, on the panel itself. A dialog
       * that leaves focus behind on the page is one a keyboard cannot
       * reach and a screen reader does not announce; focusing the
       * container gets both without putting a caret in a text field. */
      void this.panel.offsetWidth;
      this.panel.focus({ preventScroll: true });
    } else {
      this.unlock();
      document.removeEventListener('keydown', this.onKeydown);
      clearTimeout(this.searchTimer);
      if (this.controller) this.controller.abort();
      if (fromKeyboard) this.toggle.focus();
    }
  }

  /* Searching -------------------------------------------------------
   * Debounced, cached, and cancellable.
   *
   * Every keystroke is a network request if you let it be. 250ms is
   * long enough that typing a word costs one request instead of five,
   * and short enough that it still feels immediate. In-flight requests
   * are aborted rather than left to land out of order -- otherwise a
   * slow response for "de" can overwrite the results for "desk".
   */
  queueSearch() {
    clearTimeout(this.searchTimer);
    const q = this.input.value.trim();

    /* One character, not two.
     *
     * A two-character minimum is the convention for large catalogues,
     * where "a" returns thousands of rows of noise. This shop has
     * fifteen products: "d" is already a useful answer, and waiting for
     * a second keystroke just makes the first one feel broken. */
    if (q.length < 1) {
      if (this.controller) this.controller.abort();
      this.showRecommendations();
      return;
    }

    /* The first character is searched almost immediately -- there is no
       burst of typing to absorb yet, and it is the keystroke that
       decides whether the field feels alive. From the second onward the
       debounce does its job of turning a typed word into one request. */
    this.searchTimer = setTimeout(() => this.search(q), q.length === 1 ? 90 : 250);
  }

  async search(q) {
    if (this.cache.has(q)) {
      this.render(this.cache.get(q), q);
      return;
    }

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this.panel.classList.add('is-loading');

    const url =
      `${this.suggestUrl}?q=${encodeURIComponent(q)}` +
      `&resources[type]=product&resources[limit]=6&section_id=search-results`;

    try {
      const res = await fetch(url, { signal: this.controller.signal });
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      this.cache.set(q, html);
      this.render(html, q);
    } catch (err) {
      /* An abort is the expected outcome of typing quickly, not a
         failure -- only a real error should fall back. */
      if (err.name !== 'AbortError') this.showRecommendations();
    } finally {
      this.panel.classList.remove('is-loading');
    }
  }

  render(html, q) {
    if (this.input.value.trim() !== q) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const results = doc.querySelector('[data-sdrawer-results]');
    this.live.innerHTML = results ? results.outerHTML : '';
    if (this.reco) this.reco.hidden = true;
    this.panel.classList.add('has-results');
  }

  showRecommendations() {
    this.live.innerHTML = '';
    if (this.reco) this.reco.hidden = false;
    this.panel.classList.remove('has-results');
  }

  focusables() {
    return Array.from(
      this.panel.querySelectorAll('a[href], button:not([disabled]):not([hidden]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.setOpen(false, true);
      return;
    }
    if (event.key !== 'Tab') return;

    const items = this.focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onResize() {
    if (this.open && window.matchMedia('(min-width: 1400px)').matches) return;
  }

  lock() {
    this.prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  unlock() {
    if (this.prevOverflow === undefined) return;
    document.documentElement.style.overflow = this.prevOverflow;
    this.prevOverflow = undefined;
  }
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawer);
}
